import { usePage } from "@inertiajs/react"
import { motion } from "framer-motion"
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from "react"
import { AgentColumnChromeProvider } from "@/components/workspace/agent-column-chrome-context"
import { useResizablePanelWidth } from "@/hooks/use-resizable-panel-width"
import { useAgentColumnStore, type AgentColumnSection } from "@/stores/agent-column-store"
import type { AuthPageProps } from "@/types/auth"
import { cn } from "@/lib/utils"

/** Desktop split for entity pages: content | agent column. Below lg keeps floating dock. */
export const WORKSPACE_ENTITY_SPLIT_MQ = "(min-width: 1024px)"

const AGENT_WIDTH_DEFAULT = 400
const AGENT_WIDTH_MIN = 320
const AGENT_WIDTH_MAX = 800
const AGENT_MAX_RATIO = 0.4
const CONTENT_WIDTH_MIN = 360

/** Та же пружина, что у правой панели задач и настроек в AppShell. */
const AGENT_COLUMN_SPRING = { type: "spring", bounce: 0, duration: 0.3 } as const

type WorkspaceEntitySplitLayoutProps = {
  /** localStorage key for persisted agent column width (px). */
  storageKey: string
  /** Раздел для памяти свёрнутости колонки (пользователь + раздел). */
  agentSection: AgentColumnSection
  /** Main page content (strip + detail). Scrolls independently on lg+. */
  children: ReactNode
  /** Agent column — mounted on lg+, collapsed to width 0 without unmounting the chat. */
  sideColumn: ReactNode
  /** Floating bottom dock — only mounted below lg. */
  dock: ReactNode
  className?: string
  contentClassName?: string
  contentRef?: Ref<HTMLDivElement>
}

export function useWorkspaceEntitySplitDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(WORKSPACE_ENTITY_SPLIT_MQ).matches : false,
  )

  useEffect(() => {
    const mq = window.matchMedia(WORKSPACE_ENTITY_SPLIT_MQ)
    const sync = () => setIsDesktop(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  return isDesktop
}

/**
 * Entity page shell: on lg+ a resizable content | agent split; below lg a single scroll
 * region plus the existing floating bottom dock.
 */
export function WorkspaceEntitySplitLayout({
  storageKey,
  agentSection,
  children,
  sideColumn,
  dock,
  className,
  contentClassName,
  contentRef,
}: WorkspaceEntitySplitLayoutProps) {
  const isDesktop = useWorkspaceEntitySplitDesktop()
  const userId = usePage<AuthPageProps>().props.auth?.user?.id ?? null
  const register = useAgentColumnStore((state) => state.register)
  const unregister = useAgentColumnStore((state) => state.unregister)
  const registered = useAgentColumnStore((state) => state.section === agentSection)
  const collapsed = useAgentColumnStore((state) => state.section === agentSection && state.collapsed)
  const containerRef = useRef<HTMLDivElement>(null)
  const prevRegisteredRef = useRef(false)
  const prevCollapsedRef = useRef(collapsed)
  const springUntilRef = useRef(0)
  if (registered && prevRegisteredRef.current && prevCollapsedRef.current !== collapsed) {
    springUntilRef.current = performance.now() + AGENT_COLUMN_SPRING.duration * 1000
  }
  prevRegisteredRef.current = registered
  prevCollapsedRef.current = collapsed
  const collapsedToggled = performance.now() < springUntilRef.current

  useLayoutEffect(() => {
    register(userId, agentSection)
    return () => unregister(agentSection)
  }, [register, unregister, userId, agentSection])
  const {
    width: agentWidth,
    maxWidth: agentMaxWidth,
    onResizePointerDown,
  } = useResizablePanelWidth({
    storageKey,
    defaultWidth: AGENT_WIDTH_DEFAULT,
    minWidth: AGENT_WIDTH_MIN,
    maxWidth: AGENT_WIDTH_MAX,
    maxWidthRatio: AGENT_MAX_RATIO,
    minSiblingWidth: CONTENT_WIDTH_MIN,
    containerRef,
  })

  if (!isDesktop) {
    return (
      <div className={cn("flex h-full min-h-0 flex-col overflow-hidden", className)}>
        <div
          ref={contentRef}
          data-workspace-entity-content=""
          className={cn("it2tv-scrollbar min-h-0 flex-1 overflow-y-auto", contentClassName ?? "p-4")}
        >
          {children}
        </div>
        {dock}
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn("flex h-full min-h-0 overflow-hidden", className)}>
      <div
        ref={contentRef}
        data-workspace-entity-content=""
        className={cn("it2tv-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto", contentClassName ?? "p-4")}
      >
        {children}
      </div>

      <motion.div
        initial={false}
        animate={{
          width: collapsed ? 0 : agentWidth,
          opacity: collapsed ? 0 : 1,
        }}
        transition={collapsedToggled ? AGENT_COLUMN_SPRING : { duration: 0 }}
        className={cn("relative h-full min-h-0 shrink-0 overflow-x-hidden", collapsed && "pointer-events-none")}
        style={{ flexBasis: "auto" }}
        aria-hidden={collapsed || undefined}
        {...(collapsed ? { inert: true } : {})}
      >
        {collapsed ? null : (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={agentWidth}
            aria-valuemin={AGENT_WIDTH_MIN}
            aria-valuemax={Math.round(agentMaxWidth)}
            tabIndex={0}
            onPointerDown={onResizePointerDown}
            className="group absolute inset-y-0 left-0 z-20 w-3 -translate-x-1/2 cursor-ew-resize"
          >
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-ui-border transition-colors group-hover:bg-ui-accent group-active:bg-ui-accent" />
          </div>
        )}
        <div className="h-full" style={{ width: agentWidth }}>
          <AgentColumnChromeProvider>{sideColumn}</AgentColumnChromeProvider>
        </div>
      </motion.div>
    </div>
  )
}
